import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserPlus, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getInitials } from '@/utils/stringUtils';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import type { DiscoveryPlayer } from '@/hooks/useDiscoveryEngine';

interface Props {
  players: DiscoveryPlayer[];
}

export const DiscoverPlayersWidget: React.FC<Props> = ({ players }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (players.length === 0) return null;

  const handleFollow = async (e: React.MouseEvent, playerId: string, playerName: string) => {
    e.stopPropagation();
    if (!user) return;
    try {
      const { error } = await supabase
        .from('followers')
        .insert({ follower_id: user.id, following_id: playerId });
      if (error) throw error;
      toast.success(`Following ${playerName}`);
    } catch {
      toast.error('Failed to follow player');
    }
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center">
            <UserPlus className="h-4 w-4 mr-2 text-primary" />
            Discover Players
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {players.map((player) => (
          <div
            key={player.id}
            className="flex items-start gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
            onClick={() => navigate(`/profile/${player.id}`)}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={player.avatar_url || undefined} alt={player.full_name} />
              <AvatarFallback>{getInitials(player.full_name)}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{player.full_name}</div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                <span>Rating: {player.skill_rating}</span>
                {player.play_type && <span>• {player.play_type}</span>}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {player.matchReasons.slice(0, 2).map((reason) => (
                  <Badge key={reason} variant="secondary" className="text-[10px] px-1.5 py-0">
                    {reason}
                  </Badge>
                ))}
                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                  {Math.round(player.score * 100)}%
                </Badge>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={(e) => handleFollow(e, player.id, player.full_name)}
            >
              <UserPlus className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
